import { toNumberTimestamp } from '../../../util.js';
import { TICK_COUNT, timeStep } from '../scaleMath';
import { generateTimeTicks } from '../../helpers/time-helpers/timeTicks';
import { generateLinearScale } from '../generate-linear-scale/generateLinearScale';
import type { GenerateScaleProps, Scale } from '../scale.types';

export const generateTimeScale = ({ domain, range, nice = false }: GenerateScaleProps = {}): Scale<Date> | null => {
  if (!Array.isArray(domain) || !domain.length) return null;
  let first = toNumberTimestamp(domain[0]) ?? NaN;
  let last = toNumberTimestamp(domain[domain.length - 1]) ?? NaN;
  if (!Number.isFinite(first) || !Number.isFinite(last)) return null;

  if (nice && first !== last) {
    const flipped = first > last;
    const [min, max] = flipped ? [last, first] : [first, last];
    const step = timeStep(min, max, TICK_COUNT);
    const low = Math.floor(min / step) * step;
    const high = Math.ceil(max / step) * step;
    [first, last] = flipped ? [high, low] : [low, high];
  }

  const linear = generateLinearScale({ domain: [first, last], range, nice: false });
  if (!linear) return null;

  const scale = ((value: unknown) => {
    const time = toNumberTimestamp(value);
    return time === undefined ? undefined : linear(time);
  }) as Scale<Date>;

  scale.domain = () => linear.domain().map((time) => new Date(time));
  scale.range = linear.range;
  scale.ticks = (count?: number) => generateTimeTicks([first, last], count ?? 5);
  scale.invert = (position: number) => {
    const time = linear.invert(position);
    return time === undefined ? undefined : new Date(time);
  };
  scale.bandwidth = () => 0;
  scale.step = () => 0;
  scale.padding = () => 0;

  return scale;
};
